import { supabase } from "@/integrations/supabase/client";

/* ── Drip API helpers (via drip-proxy Edge Function) ─────────
   The API token never leaves the server — all requests go through
   the drip-proxy Supabase Edge Function.                        */

export interface DripDesign {
  id: string | number;
  status: string;
  preview_url?: string | null;
  file_url?: string | null;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

async function callDripProxy(
  action: "generate" | "get",
  designId: string,
  body?: Record<string, unknown>,
): Promise<unknown> {
  const { data: sessionData } = await (supabase as any).auth.getSession();
  const token = sessionData?.session?.access_token;
  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/drip-proxy?action=${action}&design_id=${encodeURIComponent(designId)}`,
    {
      method: action === "generate" ? "POST" : "GET",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(action === "generate" ? { body: JSON.stringify(body ?? {}) } : {}),
    },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error ?? `Drip proxy responded ${res.status}`);
  }
  return res.json();
}

/** Trigger Drip to render the gang sheet PNG (consumes credits). */
export async function dripGenerateImage(designId: string, fileName?: string): Promise<unknown> {
  return callDripProxy("generate", designId, fileName ? { file_name: fileName } : {});
}

/** Fetch design details (status, file_url, preview_url, etc.) */
export async function dripGetDesign(designId: string): Promise<DripDesign> {
  return callDripProxy("get", designId) as Promise<DripDesign>;
}

/* ── Types ─────────────────────────────────────────────────── */

export interface CanvasConfig {
  canvas_width_mm: number;
  min_spacing_mm: number;
  bleed_mm: number;
  price_per_m: number;
}

export const DEFAULT_CANVAS_CONFIG: CanvasConfig = {
  canvas_width_mm: 580,
  min_spacing_mm: 3,
  bleed_mm: 0,
  price_per_m: 0,
};

export interface GangSheetItem {
  id: string;
  layout_id: string;
  job_id: string | null;
  file_id: string | null;
  label: string | null;
  x_mm: number;
  y_mm: number;
  width_mm: number;
  height_mm: number;
  rotation_deg: number;
  z_index: number;
  has_white_bg: boolean;
  white_removed: boolean;
  has_cut_path: boolean;
  cut_path_data: unknown | null;
  collision_ids: string[];
  preview_url: string | null;
  original_url: string | null;
  file_name: string | null;
  source_width_px: number | null;
  source_height_px: number | null;
  created_at: string;
  updated_at: string;
}

export interface GangSheetLayout {
  id: string;
  job_id: string | null;
  product_id: string | null;
  canvas_width_mm: number;
  canvas_height_mm: number;
  used_area_mm2: number;
  linear_metres: number;
  utilisation_pct: number;
  estimated_cost: number | null;
  price_per_m: number | null;
  item_count: number;
  layout_metadata: Record<string, unknown>;
  status: "draft" | "submitted" | "approved" | "rejected";
  created_at: string;
  updated_at: string;
  items?: GangSheetItem[];
}

/* ── Client-side item (before DB save, uses File objects) ──── */

export interface LocalItem {
  id: string; // temp uuid
  file: File | null;
  preview_url: string; // object URL
  original_url: string | null;
  file_name: string;
  x_mm: number;
  y_mm: number;
  width_mm: number;
  height_mm: number;
  rotation_deg: number;
  z_index: number;
  has_white_bg: boolean;
  white_removed: boolean;
  source_width_px: number;
  source_height_px: number;
  collision_ids: string[];
}

/* ── Geometry helpers ───────────────────────────────────────── */

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** AABB overlap test (with optional gap) */
export function rectsOverlap(a: Rect, b: Rect, gap = 0): boolean {
  return (
    a.x < b.x + b.w + gap && a.x + a.w + gap > b.x && a.y < b.y + b.h + gap && a.y + a.h + gap > b.y
  );
}

/** Compute collision map: returns Set<id> of colliding pairs (flattened) */
export function computeCollisions(items: LocalItem[], spacing: number): Map<string, string[]> {
  const result = new Map<string, string[]>();
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      const a = items[i];
      const b = items[j];
      const ra: Rect = { x: a.x_mm, y: a.y_mm, w: a.width_mm, h: a.height_mm };
      const rb: Rect = { x: b.x_mm, y: b.y_mm, w: b.width_mm, h: b.height_mm };
      if (rectsOverlap(ra, rb, spacing)) {
        result.set(a.id, [...(result.get(a.id) ?? []), b.id]);
        result.set(b.id, [...(result.get(b.id) ?? []), a.id]);
      }
    }
  }
  return result;
}

/** Max Y extent of all items */
export function usedHeight(items: LocalItem[]): number {
  if (!items.length) return 0;
  return Math.max(...items.map((it) => it.y_mm + it.height_mm));
}

/** Total used area */
export function usedArea(items: LocalItem[]): number {
  return items.reduce((acc, it) => acc + it.width_mm * it.height_mm, 0);
}

/** Utilisation % against canvas area */
export function utilisation(items: LocalItem[], canvasW: number, canvasH: number): number {
  if (canvasH === 0) return 0;
  return Math.min(100, (usedArea(items) / (canvasW * canvasH)) * 100);
}

/** Linear metres from height in mm */
export function heightToMetres(heightMm: number): number {
  return Math.round((heightMm / 1000) * 1000) / 1000;
}

/* ── Magic Arrange (shelf-first bin-packing) ─────────────────
   Packs items L→R, top→bottom, using a simple shelf algorithm.
   Items are sorted largest-area-first for better utilisation. */
export function magicArrange(
  items: LocalItem[],
  canvasWidth: number,
  spacing: number,
): LocalItem[] {
  const sorted = [...items].sort((a, b) => b.height_mm - a.height_mm);
  let curX = spacing;
  let curY = spacing;
  let shelfH = 0;

  return sorted.map((item) => {
    // Does it fit on this shelf?
    if (curX + item.width_mm + spacing > canvasWidth && curX > spacing) {
      // New shelf
      curY += shelfH + spacing;
      curX = spacing;
      shelfH = 0;
    }
    const placed = { ...item, x_mm: curX, y_mm: curY };
    curX += item.width_mm + spacing;
    shelfH = Math.max(shelfH, item.height_mm);
    return placed;
  });
}

/* ── Estimate cost ──────────────────────────────────────────── */
export function estimateCost(heightMm: number, pricePerM: number): number {
  return Math.round(heightToMetres(heightMm) * pricePerM * 100) / 100;
}

/* ── White-background detection (heuristic) ─────────────────
   Draws image to offscreen canvas, samples corners + centre.
   Returns true if >90% of sampled pixels are near-white.       */
export async function detectWhiteBackground(previewUrl: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const SIZE = 64;
        canvas.width = SIZE;
        canvas.height = SIZE;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(false);
          return;
        }
        ctx.drawImage(img, 0, 0, SIZE, SIZE);
        // Sample 25 points in a grid
        const pts: [number, number][] = [];
        for (let gx = 0; gx < 5; gx++)
          for (let gy = 0; gy < 5; gy++)
            pts.push([Math.floor((gx / 4) * (SIZE - 1)), Math.floor((gy / 4) * (SIZE - 1))]);
        let whiteCount = 0;
        for (const [px, py] of pts) {
          const d = ctx.getImageData(px, py, 1, 1).data;
          if (d[0] > 240 && d[1] > 240 && d[2] > 240) whiteCount++;
        }
        resolve(whiteCount / pts.length > 0.85);
      } catch {
        resolve(false);
      }
    };
    img.onerror = () => resolve(false);
    img.src = previewUrl;
  });
}

/* ── DB functions ───────────────────────────────────────────── */

export async function getCanvasConfig(productId: string): Promise<CanvasConfig> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data } = await (supabase as any)
    .from("product_canvas_configs")
    .select("canvas_width_mm, min_spacing_mm, bleed_mm, price_per_m")
    .eq("product_id", productId)
    .maybeSingle();
  if (!data) return DEFAULT_CANVAS_CONFIG;
  return data as CanvasConfig;
}

export async function saveLayout(
  jobId: string,
  productId: string | null,
  items: LocalItem[],
  config: CanvasConfig,
  pricePerM: number,
): Promise<GangSheetLayout> {
  const h = usedHeight(items);
  const a = usedArea(items);
  const canvasW = config.canvas_width_mm;
  const canvasH = Math.max(h + config.min_spacing_mm, 50);
  const linM = heightToMetres(canvasH);
  const util = utilisation(items, canvasW, canvasH);
  const cost = estimateCost(canvasH, pricePerM);

  const payload = {
    job_id: jobId,
    product_id: productId,
    canvas_width_mm: canvasW,
    canvas_height_mm: canvasH,
    used_area_mm2: a,
    linear_metres: linM,
    utilisation_pct: util,
    estimated_cost: cost,
    price_per_m: pricePerM,
    item_count: items.length,
    layout_metadata: { spacing: config.min_spacing_mm },
    status: "submitted" as const,
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const db = supabase as any;

  // Upsert layout (one per job)
  const { data: layout, error: le } = await db
    .from("gang_sheet_layouts")
    .upsert(payload, { onConflict: "job_id" })
    .select()
    .single();
  if (le) throw le;

  // Replace items
  await db.from("gang_sheet_items").delete().eq("layout_id", layout.id);

  const itemRows = items.map((it, idx) => ({
    layout_id: layout.id,
    job_id: jobId,
    label: it.file_name,
    x_mm: it.x_mm,
    y_mm: it.y_mm,
    width_mm: it.width_mm,
    height_mm: it.height_mm,
    rotation_deg: it.rotation_deg,
    z_index: idx,
    has_white_bg: it.has_white_bg,
    white_removed: it.white_removed,
    preview_url: it.preview_url,
    original_url: it.original_url,
    file_name: it.file_name,
    source_width_px: it.source_width_px,
    source_height_px: it.source_height_px,
    collision_ids: it.collision_ids,
  }));

  if (itemRows.length > 0) {
    const { error: ie } = await db.from("gang_sheet_items").insert(itemRows);
    if (ie) throw ie;
  }

  return layout as GangSheetLayout;
}

export async function getLayout(jobId: string): Promise<GangSheetLayout | null> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data } = await (supabase as any)
    .from("gang_sheet_layouts")
    .select("*, items:gang_sheet_items(*)")
    .eq("job_id", jobId)
    .maybeSingle();
  return (data as GangSheetLayout | null) ?? null;
}
